import React, { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { addDays } from 'date-fns';
import getDate from 'date-fns/getDate';
import parseISO from 'date-fns/parseISO';
import { observer } from 'mobx-react';
import { mortgageLookups, paymentMethod, paymentType, originalGSNICStates } from '@ourbranch/lookups';
import { useStore } from 'core/store';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { FormField } from 'core/components/form';
import withDatePicker from 'core/components/with-date-picker';
import { NotificationCard } from 'core/components/notification-card';
import { canOfferMegaDownPay } from 'offer/helpers/mega-down-pay-helpers';
import useStyles from './settings.styles';

const mortgageList = mortgageLookups?.map((mortgage, index) => ({
  id: index,
  text: mortgage.correctName
}));

const CondoSettings = observer(({ paymentMethodOptions, billingDaysOptions, paymentTypeOptions }) => {
  const { offer } = useStore();
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { condo: { mortgageDetails } = {} } = values;
  const { isBix } = offer;
  const [isPaymentMortgage, setIsPaymentMortgage] = useState(values.global.condoPaymentMethod === paymentMethod.Escrow);

  const offerMegaDownPay = useMemo(
    () =>
      canOfferMegaDownPay(
        values.global.condoEffectiveDate,
        values.global.condoBillingDayOfMonth,
        values.global.condoPaymentType
      ),
    [values.global.condoEffectiveDate, values.global.condoBillingDayOfMonth, values.global.condoPaymentType]
  );

  // pre-fill the mortgage lender address based on selection
  const handleMortgageSelection = useCallback(
    (_, { text, id }) => {
      const mortgage = mortgageLookups.find((mgItem) => mgItem.correctName === text);
      const primaryIndex = mortgageDetails.findIndex((detail) => detail.primary);
      const mortgageDetailIndex = primaryIndex > -1 ? primaryIndex : mortgageDetails.length;
      if (mortgage) {
        const { address, address2, city, state, zip, correctName, id: mortgageID } = mortgage;
        const mortgageHolderAddress = {
          address,
          address2,
          city,
          state,
          zip
        };
        setFieldValue('primaryMortgageDetail.mortgageHolderAddress', mortgageHolderAddress);
        setFieldValue('primaryMortgageDetail.mortgageHolderName', correctName);
        setFieldValue('primaryMortgageDetail.mortgageHolderId', mortgageID || id);
        setFieldValue(`condo.mortgageDetails[${mortgageDetailIndex}].mortgageHolderAddress`, mortgageHolderAddress);
        setFieldValue(`condo.mortgageDetails[${mortgageDetailIndex}].mortgageHolderName`, correctName);
        setFieldValue(`condo.mortgageDetails[${mortgageDetailIndex}].id`, mortgageID || id);
        setFieldValue(`condo.mortgageDetails[${mortgageDetailIndex}].primary`, true);
      }
    },
    [setFieldValue, mortgageDetails]
  );

  const handleLoanNumberChange = ({ currentTarget: { value } }) => {
    const primaryIndex = mortgageDetails.findIndex((detail) => detail.primary);
    const mortgageDetailIndex = primaryIndex > -1 ? primaryIndex : mortgageDetails.length;
    setFieldValue(`condo.mortgageDetails[${mortgageDetailIndex}].loanNumber`, value);
    setFieldValue(`condo.mortgageDetails[${mortgageDetailIndex}].primary`, true);
  };

  useEffect(() => {
    setIsPaymentMortgage(values.global.condoPaymentMethod === paymentMethod.Escrow);
  }, [values]);

  useEffect(() => {
    // Effects to do on first render:
    // touch fields so that validation errors will show
    if (isPaymentMortgage) {
      [
        'primaryMortgageDetail.mortgageHolderName',
        'primaryMortgageDetail.loanNumber',
        'primaryMortgageDetail.mortgageHolderAddress.address',
        'primaryMortgageDetail.mortgageHolderAddress.city',
        'primaryMortgageDetail.mortgageHolderAddress.zip',
        'primaryMortgageDetail.mortgageHolderAddress.state'
      ].forEach((fieldName) => setFieldTouched(fieldName));
    }
  }, []);

  // this is used to default the billing day of the month to the day that the policy is effective
  const condoEffectiveDay = useMemo(() => {
    if (values.global.condoEffectiveDate) {
      return getDate(parseISO(values.global.condoEffectiveDate));
    }
  }, [values]);

  // if condo billing day of month is null, default the selection to the effective day of the policy
  // or the 28th if the effectiveDay is past the allowed options of days 1 - 28
  useEffect(() => {
    if (!values.global.condoBillingDayOfMonth && values.global.condoBillingDayOfMonth !== condoEffectiveDay) {
      if (condoEffectiveDay > 28) {
        setFieldValue('global.condoBillingDayOfMonth', 28);
      } else {
        setFieldValue('global.condoBillingDayOfMonth', condoEffectiveDay);
      }
    }
  }, [values, condoEffectiveDay, setFieldValue]);

  const state = offer.state;

  const showCreditCardNote =
    values.global.condoPaymentMethod === paymentMethod.ACH && !isBix && originalGSNICStates.includes(state);

  const onCondoPaymentChange = useCallback(
    (condoPaymentMethod) => {
      if (condoPaymentMethod === paymentMethod.Escrow) {
        // set payment freq to Mortgage
        setFieldValue('global.condoPaymentType', paymentType.Escrow);
      } else {
        setFieldValue('global.condoPaymentType', paymentType.OneTime);
      }
    },
    [setFieldValue]
  );

  return (
    <Section title="Condo Policy Settings" type="SubSection">
      <Card className={classes.container} type="secondary">
        <Label className={classes.subtitle} type="formSubTitle">
          Policy Term
        </Label>
        <Grid container alignItems="center">
          <FormField
            type="date"
            name="global.condoEffectiveDate"
            label="Condo policy effective date"
            xs={4}
            mode="dark"
            disableFuture={false}
            maxDate={addDays(new Date(), 60)}
            fast={false}
          />
        </Grid>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={2}>
            <Label type="formSubTitle" className={classes.subtitle}>
              Billing Details
            </Label>
          </Grid>
          {showCreditCardNote && (
            <Grid item xs={6}>
              <Label type="noteSmall">Note: There is no additional fee for paying with credit card.</Label>
            </Grid>
          )}
        </Grid>

        <Grid container>
          <Grid container spacing={4}>
            <FormField
              type="select"
              name="global.condoBillingDayOfMonth"
              label="Condo Billing Day"
              mode="dark"
              options={billingDaysOptions}
              xs={4}
            />
            <FormField
              label="Condo payment method"
              xs={4}
              type="select"
              name="global.condoPaymentMethod"
              options={paymentMethodOptions}
              onChange={onCondoPaymentChange}
              mode="dark"
            />
            <FormField
              label="Condo payment frequency"
              xs={4}
              type="select"
              name="global.condoPaymentType"
              options={paymentTypeOptions.filter((option) => {
                if (!isPaymentMortgage) {
                  return option.id !== paymentType.Escrow;
                }
                return option;
              })}
              mode="dark"
              disabled={isPaymentMortgage}
              fast={false}
            />
          </Grid>
        </Grid>

        {isPaymentMortgage && (
          <Grid container spacing={4}>
            <Grid container spacing={4}>
              <FormField
                mode="dark"
                name="primaryMortgageDetail.mortgageHolderName"
                options={mortgageList}
                onSelection={handleMortgageSelection}
                label="Lender Name *"
                type="autocomplete"
                xs={4}
                allowUserInput
                fast={false}
              />
              <FormField
                name="primaryMortgageDetail.loanNumber"
                type="string"
                label="Loan Number *"
                mode="dark"
                xs={8}
                onBlur={handleLoanNumberChange}
              />
            </Grid>
            <Grid container spacing={4}>
              <FormField
                name="primaryMortgageDetail.mortgageHolderAddress.address"
                type="string"
                label="Mortgage Holder Address *"
                xs={8}
                mode="dark"
              />
              <FormField
                name="primaryMortgageDetail.mortgageHolderAddress.address2"
                type="string"
                label="Address 2"
                xs={4}
                mode="dark"
              />
              <FormField
                name="primaryMortgageDetail.mortgageHolderAddress.city"
                type="string"
                label="City *"
                xs={4}
                mode="dark"
              />
              <FormField
                name="primaryMortgageDetail.mortgageHolderAddress.state"
                type="state"
                label="State *"
                xs={4}
                mode="dark"
              />
              <FormField
                name="primaryMortgageDetail.mortgageHolderAddress.zip"
                type="string"
                label="Zip code *"
                xs={4}
                mode="dark"
              />
            </Grid>
          </Grid>
        )}
        {offerMegaDownPay && (
          <>
            <Grid container>
              <FormField
                type="checkbox"
                name="global.condoMegaDownPay"
                id="global.condoMegaDownPay"
                label="Add second payment to down payment billing"
                fast={false}
              />
            </Grid>
            {!values.global.condoMegaDownPay && (
              <NotificationCard type="quaternary">
                <Label className={classes.warningText}>
                  The member will be charged a second payment within 20 days of making their down payment.
                </Label>
              </NotificationCard>
            )}
          </>
        )}
      </Card>
    </Section>
  );
});

CondoSettings.propTypes = {
  paymentMethodOptions: PropTypes.array.isRequired,
  billingDaysOptions: PropTypes.array.isRequired,
  paymentTypeOptions: PropTypes.array.isRequired
};

export default withDatePicker(CondoSettings);
